/**
 * Handing a file to the person, in the only way each shell can.
 *
 * The archive screen created a blob, set it on a hidden `<a download>` and
 * clicked it — the way a browser hands over a file without a server. Inside a
 * WebView that click does nothing at all: Android's DownloadListener is never
 * called for a `blob:` URL, and WKWebView has no download handler unless one
 * is registered. So on both phones the button worked, the archive was built,
 * the passphrase was accepted, and no file was ever written anywhere. The
 * screen even remembered the date of an export that did not exist.
 *
 * That mattered more than a broken button: with the ratchet, message keys are
 * destroyed after use, and this file is the only thing that survives a change
 * of phone. An export that silently produces nothing is a backup that is not
 * there on the day it is needed.
 *
 * So the shells get asked first. Both expose `saveFile(base64, name)` — the
 * bytes go over the bridge and the system's own sheet decides where they land,
 * which is also the only way a person on a phone can put the file somewhere
 * that outlives the app. A browser keeps the blob, which is right there.
 */

/** Base64 for bytes of any size — `apply` on a whole megabyte overflows the stack. */
export function toBase64(bytes) {
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  if (typeof btoa === 'function') return btoa(binary);
  return Buffer.from(binary, 'binary').toString('base64');   // node, tests
}

/**
 * Save `bytes` as `filename`. Returns 'native' or 'blob' — which road it took,
 * so the screen can say something true about where the file went.
 *
 * `anchor` is the hidden `<a>` the browser path needs; without one (or without
 * a shell) this throws rather than pretending, because a silent no-op here is
 * exactly the bug it exists to end.
 */
export function saveBytes({ bytes, filename, anchor = null, win = typeof window === 'undefined' ? null : window }) {
  const native = win && win.VoxioxNative;
  if (native && typeof native.saveFile === 'function') {
    native.saveFile(toBase64(bytes), filename);
    return 'native';
  }
  if (!anchor || typeof win?.URL?.createObjectURL !== 'function') {
    throw new Error('этому приложению некуда положить файл');
  }
  const url = win.URL.createObjectURL(new win.Blob([bytes], { type: 'application/octet-stream' }));
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  win.setTimeout(() => win.URL.revokeObjectURL(url), 60000);
  return 'blob';
}
